import React from 'react';
import { observer } from 'mobx-react';
import cx from 'classnames';
import { Navigate } from 'react-router-dom';
import { TafCheckboxInput, TafButton } from '@taf-ui/react';
import { TafChangeEvent } from '@taf-ui/core';
import AppStorage from 'Storage/Storage';
import { isBrand, translate } from 'utils/utils';
import DraftService from 'Requests/DraftService/DraftService';
import FinalizeService from 'Requests/FinalizeService/FinalizeService';
import Steps from 'enums/Steps';
import Labels from 'enums/Labels';
import { Loader } from 'Loading/Loading';
import HealthDeclarationStatus from 'enums/HealthDeclarationStatus';
import RouteParams from '../interfaces/RouteParams';
import CompleteState from '../interfaces/Complete';
import Brand from '../enums/Brand';
import { languageToReadSpeakerLang } from 'ReadSpeakerReader/ReadSpeakerProvider';
import ReadSpeakerReader from 'ReadSpeakerReader';

class Complete extends React.PureComponent<RouteParams, CompleteState> {
  private readonly storage: AppStorage;

  private readonly draftService: DraftService;

  private readonly finalizeQSService: FinalizeService;

  private pathOfInvalidPage: string | null;

  constructor(props: RouteParams) {
    super(props);
    this.storage = AppStorage.getInstance();
    this.draftService = new DraftService();
    this.finalizeQSService = new FinalizeService();
    this.pathOfInvalidPage = null;
    this.state = {
      isFinalizing: false,
      navigateComponent: undefined,
    };
  }

  componentDidMount(): void {
    this.storage.modifyProgressBar(Steps.All, 'active', false);
    this.storage.modifyProgressBar(Steps.Complete, 'active', true);
    this.checkIfWeNeedToStoreDraft();
  }

  componentWillUnmount(): void {
    this.storage.modifyProgressBar(Steps.Complete, 'done', true);
    this.storage.modifyProgressBar(Steps.Complete, 'active', false);
  }

  checkIfWeNeedToStoreDraft = (): void => {
    const currentHDStatus = this.storage.getCurrentHDStatus();
    const { isFinalizing } = this.state;
    if (
      currentHDStatus === HealthDeclarationStatus.IN_PROGRESS &&
      !isFinalizing
    ) {
      this.draftService.syncLater(500);
    }
  };

  handleIsInfoCompleteAndCorrect = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsWefoxInfoCompleteAndCorrect(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleIsConsequencesOfViolation = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsWefoxConsequencesOfViolation(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleIsDataProcessingAndConfidentiality = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsWefoxDataProcessingAndConfidentiality(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleIsDataProtection = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsWefoxDataProtection(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleIsHealthDecProvisions = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsWefoxHealthDecProvisions(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleIsInformHealthChanges = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsWefoxInformHealthChanges(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  handleCorrectnessOfMobilePhone = (
    event: CustomEvent<TafChangeEvent<boolean>>,
  ): void => {
    this.storage.setIsMobilePhoneCorrect(event.detail.value);
    this.checkIfPageValid();
    this.draftService.saveDraft();
  };

  checkIfPageValid = (): void => {
    this.storage.modifyProgressBar(
      Steps.Complete,
      'valid',
      this.getIsPageValid(),
    );
  };

  getIsPageValid = (): boolean =>
    this.storage.getIsWefoxInfoCompleteAndCorrect &&
    this.storage.getIsWefoxConsequencesOfViolation &&
    this.storage.getIsWefoxDataProcessingAndConfidentiality &&
    this.storage.getIsWefoxDataProtection &&
    this.storage.getIsWefoxHealthDecProvisions &&
    this.storage.getIsWefoxInformHealthChanges &&
    this.storage.getIsMobilePhoneCorrect;

  checkPreviousStepsValidity = (): boolean =>
    this.storage
      .getProgressBarByUnderwriterOwner()
      .filter((step) => step.step !== Steps.Complete)
      .every((step) => {
        if (!step.valid) {
          this.setPathOfInvalidPage(step.path);
        }
        return step.valid;
      });

  setPathOfInvalidPage = (path: string | null): void => {
    this.pathOfInvalidPage = path;
  };

  proceedToSigning = (): void => {
    this.setPathOfInvalidPage(null);
    this.storage.modifyProgressBar(Steps.Complete, 'done', true);
    const isWholeAppValid =
      this.checkPreviousStepsValidity() && this.getIsPageValid();
    if (isWholeAppValid) {
      this.finalizeQS();
    } else {
      this.redirectToInvalidPage();
    }
  };

  redirectToInvalidPage = (): void => {
    if (this.pathOfInvalidPage) {
      const { healthDeclarationId } = this.props;
      this.setState({
        navigateComponent: (
          <Navigate
            to={`/${healthDeclarationId}/${this.pathOfInvalidPage}`}
            replace={true}
          />
        ),
      });
    }
  };

  setIsFinalizing = (isFinalizing: boolean): void => {
    this.setState({
      isFinalizing,
    });
  };

  finalizeQS(): void {
    this.setIsFinalizing(true);
    this.storage.setIsProgressBarHidden(true);
    this.finalizeQSService
      .finalize()
      .then((response) => {
        this.setIsFinalizing(false);
        this.storage.setIsProgressBarHidden(false);
        window.location.href = response.data.result.signUrl || '';
      })
      .catch(() => {
        const { healthDeclarationId } = this.props;
        this.setState({
          navigateComponent: (
            <Navigate to={`/${healthDeclarationId}/error`} replace={true} />
          ),
        });
      });
  }

  render(): React.ReactElement {
    const {
      phonePrefix = '',
      phoneNumberBody = '',
      surname = '',
      initials = '',
      firstName = '',
    } = this.storage.getPersonalDataFormState;

    let { middleName } = this.storage.getPersonalDataFormState;

    const productSpecificProgressBarLabels =
      this.storage.getProductSpecificProgressBarLabels || [];

    if (!middleName) {
      middleName = '';
    }

    const { isFinalizing, navigateComponent } = this.state;

    if (navigateComponent) {
      return <>{navigateComponent}</>;
    }

    const lang = this.storage.getLocale;
    const phone = `${phonePrefix}${phoneNumberBody}`;

    if (!this.storage.getToken || isFinalizing) {
      return <Loader />;
    }

    const fullName = isBrand(Brand.wefox)
      ? `${firstName} ${middleName} ${surname}`
      : `${initials} ${middleName} ${surname}`;

    return (
      <div
        className="completeScreen"
        id="complete-screen"
        lang={languageToReadSpeakerLang(lang)}
      >
        <ReadSpeakerReader id="complete-screen" />
        <h1>{translate(Labels.COMPLETE_WEFOX_HEADER, lang)}</h1>
        <p>{translate(Labels.COMPLETE_WEFOX_P1, lang)}</p>

        <p>
          <span>{`${translate(Labels.COMPLETE_WEFOX_P2, lang)}, `}</span>
          <span className="bold">{`${fullName}, `}</span>
          <span>{`${translate(Labels.COMPLETE_WEFOX_P3, lang)}`}</span>
        </p>

        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsWefoxInfoCompleteAndCorrect &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsWefoxInfoCompleteAndCorrect}
            name="isInfoCompleteAndCorrect"
            inputId="isInfoCompleteAndCorrect"
            label={translate(Labels.COMPLETE_WEFOX_CHECKBOX_TITLE1, lang)}
            onTafChange={this.handleIsInfoCompleteAndCorrect}
          />
        </div>

        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsWefoxConsequencesOfViolation &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsWefoxConsequencesOfViolation}
            name="isConsequencesOfViolation"
            inputId="isConsequencesOfViolation"
            label={translate(Labels.COMPLETE_WEFOX_CHECKBOX_TITLE2, lang)}
            onTafChange={this.handleIsConsequencesOfViolation}
          />
        </div>

        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsWefoxDataProcessingAndConfidentiality &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsWefoxDataProcessingAndConfidentiality}
            name="isDataProcessingAndConfidentiality"
            inputId="isDataProcessingAndConfidentiality"
            label={`${translate(Labels.COMPLETE_WEFOX_CHECKBOX_TITLE3, lang)}`}
            onTafChange={this.handleIsDataProcessingAndConfidentiality}
          />
        </div>

        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsWefoxDataProtection &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsWefoxDataProtection}
            name="isDataProtection"
            inputId="isDataProtection"
            label={`${translate(Labels.COMPLETE_WEFOX_CHECKBOX_TITLE4, lang)}`}
            onTafChange={this.handleIsDataProtection}
          />
        </div>

        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsWefoxHealthDecProvisions &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsWefoxHealthDecProvisions}
            name="isHealthDecProvisions"
            inputId="isHealthDecProvisions"
            label={`${translate(Labels.COMPLETE_WEFOX_CHECKBOX_TITLE5, lang)}`}
            onTafChange={this.handleIsHealthDecProvisions}
          />
        </div>

        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsWefoxInformHealthChanges &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsWefoxInformHealthChanges}
            name="isHealthDecChanges"
            inputId="isHealthDecChanges"
            label={`${translate(Labels.COMPLETE_WEFOX_CHECKBOX_TITLE6, lang)}`}
            onTafChange={this.handleIsInformHealthChanges}
          />
        </div>

        <div
          className={cx('checkbox-wrapper', {
            'error-wrapper':
              !this.storage.getIsMobilePhoneCorrect &&
              this.storage.getActiveStep?.done,
          })}
        >
          <TafCheckboxInput
            value={this.storage.getIsMobilePhoneCorrect}
            name="isMobilePhoneCorrect"
            inputId="isMobilePhoneCorrect"
            label={`${translate(Labels.COMPLETE_CHECKBOX_TITLE3, lang)} <span style="font-weight:bold">${phone}</span>`}
            onTafChange={this.handleCorrectnessOfMobilePhone}
          />
        </div>

        <p>
          {translate(Labels.COMPLETE_WEFOX_P4, lang, [
            (productSpecificProgressBarLabels[1] || '').toUpperCase(),
          ])}
        </p>
        <p>{translate(Labels.COMPLETE_WEFOX_P5, lang)}</p>
        <p>{translate(Labels.COMPLETE_WEFOX_P6, lang)}</p>
        <div className="signingBtn">
          <TafButton
            id="goToSigning"
            onClick={this.proceedToSigning}
            type="button"
            primary={true}
            disabled={isFinalizing}
            title={translate(Labels.PROCEED_TO_SIGNING, lang)}
          >
            {translate(Labels.PROCEED_TO_SIGNING_WEFOX, lang)}
          </TafButton>
        </div>
      </div>
    );
  }
}

export default observer(Complete);
